import React, { useEffect, useState } from "react";
import { Calendar as CalendarIcon, Plus, Clock } from "lucide-react";
import { Page, Card, Pill, PrimaryButton, EmptyState } from "../components/UI";
import api from "../lib/api";
import { DateTimePicker } from "../components/DatePicker";
import { WaitlistPanel } from "../components/WaitlistPanel";
import { toast } from "sonner";

export default function Scheduling() {
  const [clients, setClients] = useState([]);
  const [appts, setAppts] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ client_id: "", start_time: "", end_time: "", appointment_type: "individual", notes: "", status: "scheduled" });

  const load = () => Promise.all([
    api.get("/clients").then(({ data }) => setClients(data)),
    api.get("/appointments").then(({ data }) => setAppts(data)),
  ]);
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.start_time || !form.end_time) return toast.error("Please choose a start and end time");
    if (form.end_time <= form.start_time) return toast.error("End time must be after start time");
    try {
      await api.post("/appointments", form);
      toast.success("Appointment scheduled");
      setModal(false);
      setEndTouched(false);
      setForm({ client_id: "", start_time: "", end_time: "", appointment_type: "individual", notes: "", status: "scheduled" });
      load();
    } catch { toast.error("Failed"); }
  };

  const updateStatus = async (id, status) => {
    await api.put(`/appointments/${id}`, { status });
    toast.success(`Marked ${status}`);
    load();
  };

  const remove = async (id) => {
    await api.delete(`/appointments/${id}`);
    toast.success("Cancelled");
    load();
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const [endTouched, setEndTouched] = useState(false);
  const sendReminder = async (id) => {
    try {
      const { data } = await api.post(`/appointments/${id}/send-reminder`);
      const r = data.results.map((x) => `${x.channel}: ${x.status === "sent" ? "sent" : x.status === "skipped_not_configured" ? "not set up yet" : "failed"}`).join(" · ");
      (data.results.some((x) => x.status === "failed") ? toast.warning : toast.success)(`Reminder — ${r}`);
    } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Reminder failed"); }
  };
  const setEnd = (e) => { setEndTouched(true); setForm({ ...form, end_time: e.target.value }); };
  const setStart = (e) => {
    const start = e.target.value;
    let end = form.end_time;
    if (start && (!endTouched || !end || end <= start)) {
      const d = new Date(start);
      d.setMinutes(d.getMinutes() + 50);
      const pad = (n) => String(n).padStart(2, "0");
      end = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }
    setForm({ ...form, start_time: start, end_time: end });
  };

  const grouped = {};
  appts.forEach(a => {
    const d = new Date(a.start_time).toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" });
    if (!grouped[d]) grouped[d] = [];
    grouped[d].push(a);
  });

  const noShows = appts.filter(a => a.status === "no-show").length;

  return (
    <Page
      title="Scheduling"
      subtitle="Calendar, reminders, waitlist, and no-show tracking."
      actions={<PrimaryButton onClick={() => setModal(true)} testid="new-appt-btn"><span className="flex items-center gap-2"><Plus className="w-4 h-4" /> New Appointment</span></PrimaryButton>}
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
        <Card delay={0.05} className="!p-5"><div className="text-xs uppercase tracking-wider mb-1" style={{ color: "var(--mf-text-muted)" }}>Total</div><div className="font-serif text-3xl">{appts.length}</div></Card>
        <Card delay={0.1} className="!p-5"><div className="text-xs uppercase tracking-wider mb-1" style={{ color: "var(--mf-text-muted)" }}>Scheduled</div><div className="font-serif text-3xl">{appts.filter(a => a.status === "scheduled").length}</div></Card>
        <Card delay={0.15} className="!p-5"><div className="text-xs uppercase tracking-wider mb-1" style={{ color: "var(--mf-text-muted)" }}>Completed</div><div className="font-serif text-3xl">{appts.filter(a => a.status === "completed").length}</div></Card>
        <Card delay={0.2} className="!p-5"><div className="text-xs uppercase tracking-wider mb-1" style={{ color: "var(--mf-text-muted)" }}>No-Shows</div><div className="font-serif text-3xl" style={{ color: noShows > 2 ? "#B45253" : undefined }}>{noShows}</div></Card>
      </div>

      {appts.length === 0 ? <EmptyState icon={CalendarIcon} title="No appointments" description="Schedule your first session to see it here." action={<PrimaryButton onClick={() => setModal(true)}>New Appointment</PrimaryButton>} /> : (
        <div className="space-y-5">
          {Object.keys(grouped).map((day, i) => (
            <Card key={day} delay={0.25 + i * 0.05} testid={`day-group-${i}`}>
              <h3 className="font-serif text-xl mb-3">{day}</h3>
              <div className="space-y-2">
                {grouped[day].map((a) => {
                  const client = clients.find(c => c.id === a.client_id);
                  return (
                    <div key={a.id} className="p-3 rounded-xl flex flex-wrap items-center gap-3" style={{ background: "var(--mf-muted)" }} data-testid={`appt-${a.id}`}>
                      <Clock className="w-4 h-4" style={{ color: "var(--mf-primary)" }} />
                      <div className="text-sm font-medium">{new Date(a.start_time).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</div>
                      <div className="text-sm flex-1">{client ? `${client.first_name} ${client.last_name}` : "Unknown client"}</div>
                      <Pill variant="primary">{a.appointment_type}</Pill>
                      <Pill variant={a.status === "completed" ? "success" : a.status === "no-show" ? "danger" : "warn"}>{a.status}</Pill>
                      {a.status === "scheduled" && <div className="flex gap-1">
                        <button onClick={() => updateStatus(a.id, "completed")} className="text-xs px-2 py-1 rounded-full" style={{ background: "rgba(123,174,127,0.2)", color: "#4a7a4e" }} data-testid={`complete-${a.id}`}>Complete</button>
                        <button onClick={() => updateStatus(a.id, "no-show")} className="text-xs px-2 py-1 rounded-full" style={{ background: "rgba(201,168,76,0.2)", color: "#8a6f20" }} data-testid={`noshow-${a.id}`}>No-Show</button>
                        <button onClick={() => sendReminder(a.id)} className="text-xs px-2 py-1 rounded-full" style={{ background: "rgba(30,78,92,0.12)", color: "var(--mf-primary)" }} data-testid={`remind-${a.id}`}>Remind</button>
                        <button onClick={() => remove(a.id)} className="text-xs px-2 py-1 rounded-full" style={{ background: "rgba(220,38,38,0.1)", color: "#b91c1c" }} data-testid={`cancel-${a.id}`}>Cancel</button>
                      </div>}
                    </div>
                  );
                })}
              </div>
            </Card>
          ))}
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setModal(false)}>
          <div className="mf-card w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-serif text-2xl mb-4">New Appointment</h2>
            <form onSubmit={create} className="space-y-3">
              <select required value={form.client_id} onChange={set("client_id")} className="w-full px-4 py-2.5 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="new-appt-client">
                <option value="">Select client…</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
              </select>
              <div className="text-xs" style={{ color: "var(--mf-text-muted)" }}>Starts</div>
              <DateTimePicker value={form.start_time} onChange={setStart} className="w-full px-4 py-2.5 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="new-appt-start" />
              <div className="text-xs" style={{ color: "var(--mf-text-muted)" }}>Ends</div>
              <DateTimePicker value={form.end_time} onChange={setEnd} className="w-full px-4 py-2.5 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="new-appt-end" />
              <select value={form.appointment_type} onChange={set("appointment_type")} className="w-full px-4 py-2.5 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="new-appt-type">
                <option value="individual">Individual</option>
                <option value="group">Group</option>
                <option value="crisis">Crisis</option>
                <option value="medication">Medication Check</option>
                <option value="telehealth">Telehealth</option>
              </select>
              <input placeholder="Notes (optional)" value={form.notes} onChange={set("notes")} className="w-full px-4 py-2.5 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="new-appt-notes" />
              <div className="flex items-center justify-end gap-2 pt-2">
                <button type="button" onClick={() => setModal(false)} className="px-4 py-2 text-sm rounded-full">Cancel</button>
                <PrimaryButton type="submit" testid="submit-new-appt">Schedule</PrimaryButton>
              </div>
            </form>
          </div>
        </div>
      )}
      <WaitlistPanel clients={clients} />
    </Page>
  );
}
